import Backbone from 'backbone';
import dataLayerHelper from 'lib/helpers/data_layer';
import dustjs from 'dustjs';

export const AsyncProductDropView = Backbone.View.extend({
	initialize(options) {
		this.recentSearchData = null;
		this.parentView = options.parentView;
		this.siblingViews = options.siblingViews;
		this.$productGridContainer = options.$productGridContainer;
		this.$sortByDropdownElement = this.parentView.$el.find('#sort-by');
		this.isCollectionSearch = window.dataLayer.isCollectionSearch;

		this._setupListeners();
		this._asyncDropTemplates = import('./async_product_drop_templates').catch(window.clientErrorHandler);

		if (window.history) {
			this._setupPopStateHandler();
		}
		window.BCOM.util.omnitureLoaded(this._replaceInitialState.bind(this));

		if (this.parentView.childViews.similarSearches) {
			this.siblingViews.similarSearches = this.parentView.childViews.similarSearches;
		}
	},

	_setupListeners() {
		// using anonymous functions here to ignore the data being passed along with the trigger event
		this.listenTo(Backbone, 'dropdown:option:click', () => this.fetch());
		this.listenTo(Backbone, 'pagination:changed', () => this._onPaginationChange());
	},

	_onPaginationChange() {
		return this.fetch().then(() => this.scrollToTop());
	},

	_setupPopStateHandler() {
		window.onpopstate = (popState) => {
			if (popState.state) {
				const state = popState.state;
				state.isPopStateEvent = true;
				this.fetch(state);
			} else {
				return true;
			}
		};
	},

	_isSortByChange(state) {
		// check the current query params to see if they are different from the selected value of the sortBy view
		if (state.queryParams) {
			const selectBoxValue = this.$sortByDropdownElement.find('.js-selected').data('value');
			const sortParam = state.queryParams.find((param) => param.substr(0, 2) === 's=');
			return selectBoxValue && sortParam && sortParam.slice(2) !== selectBoxValue;
		}
	},

	_updateSortByView(state) {
		const sortParam = state.queryParams.find((param) => param.substr(0, 2) === 's=').slice(2);
		const $updatedSortByOption = this.$sortByDropdownElement.find(`*[data-value="${sortParam}"]`);
		this.siblingViews.sortBy.setSelectedByElement($updatedSortByOption, false);
	},

	_changeView: function(view) {
		this.parentView.view = view || this.parentView._getNextView();
		this.postViewType(this.parentView.view);
		const template = 'store/global/pages/categories/_productgrid_display_button',
			data = {
				view: this.parentView.view,
				isBuild: window.BCOM.theme.isBuild
			};

		dustjs.render(template, data, (error, html) => {
			if (error) {
				window.clientErrorHandler(error);
			}
			this.parentView.$viewButtonContainer.html(html);
		});
	},

	_pushHistoryState(queryParams) {
		if (window.history) {
			window.history.pushState(
				{ queryParams, view: this.parentView.view },
				'',
				`${window.location.pathname}?${queryParams.join('&')}`
			);
		}
	},

	_replaceHistoryState(state, url) {
		if (window.history) {
			window.history.replaceState(state, '', url);
		}
	},

	_getQueryString() {
		return window.location.search.slice(1);
	},

	_createNewUrl(params) {
		return `${window.location.pathname}?${params.toString()}`;
	},

	_getBaseQueryParams(returnDefault) {
		const queryParams = [],
			sortBy = returnDefault ? 'SCORE' : this.siblingViews.sortBy.getValue(),
			dl = window.dataLayer;
		let pageSize = 0,
			page = 0;

		if (this.siblingViews.pagination) {
			pageSize = this.siblingViews.pagination.getPageSize();
			page = this.siblingViews.pagination.getActivePage();
		}

		pageSize = pageSize || (dl.pagination && dl.pagination.pageSize) || 48;

		queryParams.push(`r=${pageSize}`);

		if (sortBy || dl.sortBy) {
			queryParams.push(`s=${sortBy || dl.sortBy.selectedValue}`);
		}

		if (page) {
			queryParams.push(`p=${page}`);
		}

		if (dl.categoryId) {
			queryParams.push(`categoryId=${dl.categoryId}`);
		}

		return queryParams;
	},

	getAllQueryParams() {
		const currentQueryParams = this.siblingViews.facetViews.getFacetQueries(),
			additionalQueryParams = this._getBaseQueryParams(),
			dl = window.dataLayer;

		if (dl.search) {
			currentQueryParams.push(`term=${dl.search.name}`);
		}

		return currentQueryParams.concat(additionalQueryParams);
	},

	handleChangeViewClick() {
		this._asyncDropTemplates.then(() => {
			this._changeView();
			const queryParams = this.getAllQueryParams();
			this._pushHistoryState(queryParams);
			if (this.recentSearchData) {
				this._reRender(this.recentSearchData);
			} else {
				this.fetch({ queryParams });
			}
		});
	},

	_replaceInitialState() {
		// Fill in any missing base query params.
		const existingQueryString = this._getQueryString();
		const params = new URLSearchParams(existingQueryString);

		const defaultQueryParams = this._getBaseQueryParams(true);
		for (let qp of defaultQueryParams) {
			const [key, value] = qp.split('=');
			if (!params.has(key)) {
				params.set(key, value);
			}
		}

		const url = this._createNewUrl(params);
		const replaceStateOptions = { queryParams: params.toString().split('&'), view: this.parentView.view };
		this._replaceHistoryState(replaceStateOptions, url);
	},

	_reRender: function(results) {
		const dustData = {
			results,
			enableElevatedSearchSuggestions: window.dataLayer.enableElevatedSearchSuggestions,
			view: this.parentView.view,
			device: window.BCOM.device,
			isBuild: window.BCOM.theme.isBuild,
			BCOM: window.BCOM,
			isQuickShipEnabled: window.dataLayer.isQuickShipEnabled,
			spacerImage: '/public/shared/images/transparent.png',
			inlineRecommendationsEnabled: Boolean(window.dataLayer.inlineRecommendationsId)
		};
		const template = window.BCOM.device.isDesktop
			? 'store/desktop/pages/categories/_product_tiles'
			: 'store/global/pages/categories/_product_tiles';

		dustjs.render(template, dustData, (err, html) => {
			if (!err) {
				this.$productGridContainer.html(html).removeClass('no-click-events is-loading');
				this._reInit(results);
				if (!window.dataLayer.abLimitedScrollToTop) {
					// If limited scroll to top wins the A/B test, remove this entire if-block.
					this.scrollToTop();
				}
			} else {
				console.warn(err);
			}
		});
		if (this.siblingViews.similarSearches && dustData.enableElevatedSearchSuggestions) {
			this.siblingViews.similarSearches.setElement(this.parentView.$('.js-related-searches'));
			this.siblingViews.similarSearches.render();
		}
	},

	scrollToTop() {
		$.scrollTo('.productgrid-header');
	},

	_reInit(results) {
		this.siblingViews.facetViews.updateFacetCollection(results);
		this.parentView.$filterBoxButton.text(
			`Filter${results.selectedFacets && results.selectedFacets.length ? ` (${results.selectedFacets.length})` : ''}`
		);
		if (results.search) {
			this.parentView.$searchTerm.text(results.search.name);
		}
		this.parentView.$searchCount.text(results.info ? results.info.countFormatted : 0);

		if (results.products && Array.isArray(results.products)) {
			window.dataLayer.products = dataLayerHelper.trimProducts(results.products);
		}

		this.siblingViews.pagination.model.set(results.pagination);
		this.siblingViews.pagination.render();

		this.siblingViews.sortBy.render();

		if (this.siblingViews.facetViews.facetBanners) {
			this.siblingViews.facetViews.facetBanners._resetActiveBanners();
		}

		$('.js-finish-popover').popover();

		if (!window.BCOM.device.isMobile) {
			this.parentView._enablePersistantNavBanners($('.nav-tabs a[data-toggle="tab"]'), 'tab');
		}
		if (this.siblingViews.facetViews.quickAdd) {
			this.siblingViews.facetViews.quickAdd.initialize();
		}
		// init for each compare button on the new products
		window.BCOM.views.baseView.compareWidgetView.initButtons(this.$productGridContainer);
	},

	_trackShippingAsyncDrops: function(trackingMessage) {
		Backbone.trigger('omniture:shippingAsyncDropsChange', trackingMessage);
	},

	postViewType(view) {
		$.ajax({
			type: 'POST',
			url: `/app/api/search/viewtype?view=${view}`
		});
	},

	fetch(state) {
		const isPopStateEvent = state && state.isPopStateEvent,
			isSortByChange = state && this._isSortByChange(state),
			url = `/app/api/search/products?`,
			queryParams = !state ? this.getAllQueryParams() : state.queryParams;
		this.$productGridContainer.addClass('no-click-events is-loading');

		if (isPopStateEvent && isSortByChange) {
			this._updateSortByView(state);
		}

		if (!state) {
			this._pushHistoryState(queryParams);
		} else if (state.view) {
			this._changeView(state.view);
		}

		return $.ajax({
			type: 'GET',
			url: `${url}${queryParams.join('&')}`,
			data: {
				// sending this along so search API controller can set correct cookie value for LASTSEARCHPAGE
				// this will be filtered out of the search request params via utilities > filterSelectedFacetList
				lastSearchPage: encodeURIComponent(window.location.pathname + window.location.search),
				isCollectionSearch: this.isCollectionSearch
			}
		}).then(
			(data) => {
				this.recentSearchData = data;
				this._reRender(data);
				if (data.shippingTrackingMessage) {
					this._trackShippingAsyncDrops(data.shippingTrackingMessage);
				}
			},
			() => {
				this.$productGridContainer.removeClass('no-click-events is-loading');
			}
		);
	}
});
